<template>
  <div class="pt-4 px-4">
    <div class="mb-3 d-flex justify-content-between">
      <div class="d-flex align-items-center">
        <h2>New Backfill Job</h2>
        <button 
          class="btn btn-sm btn-primary ms-2" 
          @click="testConnection"
          :disabled="testingConnection"
        >
          {{ testingConnection ? 'Testing...' : 'Test Connection' }}
        </button>
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label">Customer</label>
      <select class="form-select" v-model="job.customerId">
        <option v-for="customer in model.customers" :value="customer.customerId">
          {{ customer.customerName }}
        </option>
      </select>
    </div>

    <div class="mb-3">
      <label class="form-label">Market Name(s)</label>
      <select-dropdown :list="selectableMarkets" label-field="marketName"
        list-title="Markets" @selected="onMarketsSelected"></select-dropdown>
    </div>

    <div class="mb-3">
      <label class="form-label">Job Type</label>
      <select class="form-select" v-model="job.jobType">
        <option value="">Select job type</option>
        <option value="DIGITAL TRANSIT">DIGITAL TRANSIT</option>
        <option value="DIGITAL STATIONARY">DIGITAL STATIONARY</option>
        <option value="STATIC TRANSIT">STATIC TRANSIT</option>
      </select>
    </div>

    <div class="mb-3" v-if="isTransitJob">
      <label class="form-label">Provider</label>
      <input 
        v-model="job.provider" 
        type="number" 
        class="form-control"
        placeholder="Enter provider ID"
      >
    </div>

    <div class="mb-3">
      <label class="form-label">Files</label>
      <input 
        type="file" 
        class="form-control" 
        @change="handleFileUpload" 
        multiple
        :accept="acceptedFileTypes"
      >
      <small class="form-text text-muted" v-if="expectedHeaders.length">
        Expected headers: {{ expectedHeaders.join(', ') }}
      </small>
    </div>

    <div class="mb-3">
      <button 
        type="button" 
        class="btn btn-secondary" 
        style="min-width: 150px;" 
        @click="onSaveClick"
        :disabled="loading"
      >
        {{ loading ? 'Saving...' : 'Save' }}
      </button>

      <router-link :to="{ name: 'pipeline' }" class="btn btn-light ms-2">
        Cancel
      </router-link>
    </div>
  </div>
</template>

<script>
import BackfillService from "@/services/BackfillService";
import AppUtil from "@/utils/AppUtil";
import streetmetrics from "@/model";
import { computed, watch } from 'vue';
import SelectDropdown from "@/components/SelectDropdown";

export default {
  name: 'BackfillJobForm',
  components: { SelectDropdown },
  data() {
    return {
      model: streetmetrics.model,
      service: new BackfillService(),
      loading: false,
      testingConnection: false,
      selectableMarkets: [],
      job: {
        customerId: null,
        marketId: null,
        jobType: '',
        provider: '',
        files: []
      }
    }
  },
  mounted() {
    // Initialize selectable markets
    this.selectableMarkets = this.model.markets
      .filter(market => market.metaData && market.metaData.isActive)
      .map(market => ({
        ...market,
        selected: false
      }))
      .sort((a, b) => a.marketName.localeCompare(b.marketName));
  },
  computed: {
    isTransitJob() {
      return this.job.jobType === 'DIGITAL TRANSIT' || this.job.jobType === 'STATIC TRANSIT';
    },
    acceptedFileTypes() {
      return '.csv';  // All job types now accept CSV files
    },
    expectedHeaders() {
      switch(this.job.jobType) {
        case 'DIGITAL TRANSIT':
          return ['date', 'campaignid', 'inventoryid', 'creative', 'duration_s', 'startlat', 'startlong'];
        case 'DIGITAL STATIONARY':
          return ['SCREEN_ID', 'TIMESTAMP', 'DURATION', 'CREATIVE_NAME', 'CREATIVE_ID', 'CONTRACT_ID'];
        //case 'STATIC TRANSIT':
        //  return ['provider_id', 'asset_id', 'route_id', 'start_date', 'end_date'];
        default:
          return [];
      }
    },
    activeMarkets() {
      return this.model.markets.filter(market => market.metaData && market.metaData.isActive)
        .sort((a, b) => a.marketName.localeCompare(b.marketName));
    }
  },
  watch: {
    'job.jobType': {
      handler(newType) {
        if (newType === 'DIGITAL STATIONARY') {
          this.model.toast.background = 'bg-yellow';
          this.model.toast.message = 'Warning: Frame onboarding must be completed before backfilling digital frame data';
        }
      },
      immediate: true
    }
  },
  methods: {
    onMarketsSelected(selectedMarkets) {
      this.job.marketId = selectedMarkets.length > 0 ? selectedMarkets[0].marketId : null;
    },
    async testConnection() {
      this.testingConnection = true;
      try {
        const response = await this.service.testConnection();
        this.model.toast.background = 'bg-green';
        this.model.toast.message = `Connection successful: ${response}`;
      } catch (error) {
        console.error('Connection test failed:', error);
        this.model.toast.background = 'bg-red';
        this.model.toast.message = 'Failed to connect to backfill service';
      } finally {
        this.testingConnection = false;
      }
    },

    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      
      // Validate file types - now only accepting CSV for all job types
      const invalidFiles = files.filter(file => {
        const ext = file.name.split('.').pop().toLowerCase();
        return ext !== 'csv';
      });

      if (invalidFiles.length > 0) {
        this.model.toast.background = 'bg-red';
        this.model.toast.message = 'Invalid file type(s). Only .csv files are accepted';
        event.target.value = ''; // Clear the file input
        return;
      }

      this.job.files = files;
    },

    async onSaveClick() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;
      
      try {
        const formData = new FormData();
        this.job.files.forEach(file => {
          formData.append('files', file);
        });
        formData.append('customer', this.job.customerId.toString());
        
        // Add single selected market
        const selectedMarket = this.model.markets.find(m => m.marketId === this.job.marketId);
        if (selectedMarket) {
          formData.append('marketName', selectedMarket.marketName);
        }
        
        formData.append('jobType', this.job.jobType);
        if (this.isTransitJob && this.job.provider) {
          formData.append('provider', this.job.provider.toString());
        }
        formData.append('runBy', this.model.user.displayName);

        // Enhanced debugging
        console.log('Preparing to upload files:', {
          numberOfFiles: this.job.files.length,
          customerId: this.job.customerId,
          selectedMarket: this.model.markets.find(m => m.marketId === this.job.marketId)?.marketName,
          jobType: this.job.jobType,
          provider: this.job.provider,
          runBy: this.model.user.displayName
        });

        for (let pair of formData.entries()) {
          console.log('FormData entry:', pair[0], pair[1]);
        }

        try {
          const response = await this.service.uploadFiles(formData);
          console.log('Upload response:', response);
        } catch (uploadError) {
          console.error('Upload error details:', {
            status: uploadError.response?.status,
            statusText: uploadError.response?.statusText,
            data: uploadError.response?.data,
            message: uploadError.message
          });
          throw uploadError;
        }

        this.model.toast.background = 'bg-green';
        this.model.toast.message = 'Backfill job submitted successfully';
        this.$router.push({ name: 'pipeline' });
      } catch (error) {
        console.error('Error submitting backfill job:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
        this.model.toast.background = 'bg-red';
        this.model.toast.message = `Error submitting backfill job: ${errorMessage}`;
      } finally {
        this.loading = false;
      }
    },

    validateForm() {
      if (!this.job.customerId) {
        this.model.toast.background = 'bg-red';
        this.model.toast.message = 'Please select a customer';
        return false;
      }
      if (!this.job.marketId) {
        this.model.toast.background = 'bg-red';
        this.model.toast.message = 'Please select a market';
        return false;
      }
      if (!this.job.jobType) {
        this.model.toast.background = 'bg-red';
        this.model.toast.message = 'Please enter a job type';
        return false;
      }
      if (this.isTransitJob && !this.job.provider) {
        this.model.toast.background = 'bg-red';
        this.model.toast.message = 'Please select a provider';
        return false;
      }
      if (!this.job.files.length) {
        this.model.toast.background = 'bg-red';
        this.model.toast.message = 'Please select at least one file';
        return false;
      }
      return true;
    }
  }
}
</script>
